import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type AuditEntry } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column, type BadgeTone } from '@/components/ui';
import { IconLogs } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';

const PAGE = 50;

// A staff action → badge tone. Failures/denials and destructive actions read
// "warn"; approvals/credits/verifications read "ok"; everything else neutral.
function actionTone(action: string, result: string): BadgeTone {
  if (result && result !== 'ok') return 'warn';
  if (/reject|ban|block|deny|denied|delete|remove|suspend/i.test(action)) return 'warn';
  if (/approve|credit|verify|create|grant|adjust/i.test(action)) return 'ok';
  return 'neutral';
}

// Pretty-print the detail JSON string; fall back to the raw text if it isn't
// valid JSON (it always should be, but be defensive).
function prettyDetail(detail?: string): string {
  if (!detail || detail === '{}') return '—';
  try {
    return JSON.stringify(JSON.parse(detail), null, 2);
  } catch {
    return detail;
  }
}

/** Audit Log — the immutable staff-action trail from admin-svc `admin_audit_log`.
 *  Records every sign-in + every back-office write with the FULL accountable
 *  picture: who / what / target / result / when, plus the request metadata (IP,
 *  user-agent, method, route) and the action's detail JSON. Filterable + paged.
 *  Gated on audit.read (the nav entry + admin-svc both enforce it). */
export function Audit() {
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState('');
  const [result, setResult] = useState<'' | 'ok' | 'error'>('');
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'audit', offset, q, result],
    queryFn: ({ signal }) =>
      adminApi.audit({ limit: PAGE, offset, q: q || undefined, result: result || undefined }, signal),
    placeholderData: (prev) => prev,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasNext = offset + items.length < total;

  const resetTo = (fn: () => void) => {
    fn();
    setOffset(0);
  };

  const columns: Array<Column<AuditEntry>> = [
    { key: 'when', header: 'When', cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span> },
    { key: 'actor', header: 'Staff', cell: (r) => <span className="text-sm text-slate-100">{r.actor_email || '—'}</span> },
    { key: 'action', header: 'Action', cell: (r) => <Badge tone={actionTone(r.action, r.result)}>{r.action}</Badge> },
    {
      key: 'target',
      header: 'Target',
      cell: (r) => (
        <span className="metric text-xs text-slate-300">
          {r.target_type}
          {r.target_id ? ` · ${r.target_id}` : ''}
        </span>
      ),
    },
    { key: 'ip', header: 'IP', cell: (r) => <span className="metric text-xs text-muted">{r.request_ip || '—'}</span> },
    {
      key: 'route',
      header: 'Route',
      cell: (r) =>
        r.http_route ? (
          <span className="metric text-[11px] text-muted">
            {r.http_method} {r.http_route}
          </span>
        ) : (
          <span className="text-xs text-muted">—</span>
        ),
    },
    { key: 'result', header: 'Result', cell: (r) => <Badge tone={r.result === 'ok' ? 'ok' : 'warn'}>{r.result || 'ok'}</Badge> },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          Details
        </button>
      ),
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Audit Log"
        subtitle="Every staff sign-in and back-office change — who, what, target, result, where from (IP), and full detail."
        right={
          <div className="flex items-center gap-2">
            <select
              value={result}
              onChange={(e) => resetTo(() => setResult(e.target.value as '' | 'ok' | 'error'))}
              className="input text-sm"
            >
              <option value="">All results</option>
              <option value="ok">OK</option>
              <option value="error">Errors</option>
            </select>
            <SearchBox
              value={q}
              onChange={(v) => resetTo(() => setQ(v))}
              placeholder="Search actor / action / target / IP…"
            />
          </div>
        }
      />
      <Card flush>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner label="Loading audit log…" />
          </div>
        ) : isError ? (
          <EmptyState title="Couldn't load the audit log" description={(error as Error)?.message} />
        ) : (
          <>
            <DataTable<AuditEntry>
              columns={columns}
              rows={items}
              rowKey={(r) => String(r.id)}
              stickyHeader
              empty={
                <EmptyState
                  title="No activity"
                  description="Staff sign-ins and back-office actions will appear here as they happen."
                  icon={<IconLogs className="h-6 w-6" />}
                />
              }
            />
            <div className="flex items-center justify-between border-t border-ink-700 px-4 py-3 text-xs text-muted">
              <span>
                {total > 0 ? `Showing ${offset + 1}–${offset + items.length} of ${total}` : 'No matches'}
                {isFetching ? ' · …' : ''}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={offset === 0 || isFetching}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}
                  className="rounded-md border border-ink-700 px-3 py-1 text-slate-200 hover:bg-ink-800 disabled:opacity-40"
                >
                  Newer
                </button>
                <button
                  type="button"
                  disabled={!hasNext || isFetching}
                  onClick={() => setOffset(offset + PAGE)}
                  className="rounded-md border border-ink-700 px-3 py-1 text-slate-200 hover:bg-ink-800 disabled:opacity-40"
                >
                  Older
                </button>
              </div>
            </div>
          </>
        )}
      </Card>

      {selected ? <AuditDetailPanel entry={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

/** Full record for one audit row — every captured field, including the request
 *  metadata and the action's detail JSON, in a dismissable panel. */
function AuditDetailPanel({ entry, onClose }: { entry: AuditEntry; onClose: () => void }) {
  const rows: Array<[string, string]> = [
    ['When', dateTime(entry.created_at)],
    ['Staff', entry.actor_email || '—'],
    ['Action', entry.action],
    ['Target', `${entry.target_type}${entry.target_id ? ` · ${entry.target_id}` : ''}`],
    ['Result', entry.result || 'ok'],
    ['IP address', entry.request_ip || '—'],
    ['Method · Route', entry.http_route ? `${entry.http_method} ${entry.http_route}` : '—'],
    ['User agent', entry.user_agent || '—'],
  ];
  return (
    <Card className="mt-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-100">Audit entry #{entry.id}</div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-slate-300 hover:bg-ink-800"
        >
          Close
        </button>
      </div>
      <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex flex-col">
            <span className="text-[11px] uppercase tracking-wide text-text-muted">{k}</span>
            <span className="metric break-all text-sm text-slate-200">{v}</span>
          </div>
        ))}
      </div>
      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-wide text-text-muted">Detail</div>
        <pre className="mt-1 max-h-72 overflow-auto rounded-md border border-ink-700 bg-ink-900 p-3 text-[11px] leading-relaxed text-slate-300">
          {prettyDetail(entry.detail)}
        </pre>
      </div>
    </Card>
  );
}
